import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const MechanicAuthContext = createContext(null);
const API_URL = process.env.REACT_APP_BACKEND_URL;

export const MechanicAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('mechanic_token'));
  const [loading, setLoading] = useState(true);
  const [currentWorkspace, setCurrentWorkspace] = useState(null);

  const buildWorkspaceFromUser = useCallback((u) => {
    if (!u?.organization_id) return null;
    return {
      id: u.organization_id,
      name: u.organization_name || 'Mon atelier',
      is_own: true,
    };
  }, []);

  const hasModule = useCallback((moduleKey) => {
    if (!user) return false;
    const modules = user.access_modules || [];
    if (['atelier', 'admin_entretiens', 'super_admin'].includes(user.role)) return true;
    return modules.includes(moduleKey);
  }, [user]);

  const checkAuth = useCallback(async () => {
    if (!token) { setLoading(false); return; }
    try {
      const res = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const u = res.data;
      const allowedRoles = ['mechanic', 'atelier', 'admin_entretiens', 'super_admin'];
      const modules = u.access_modules || [];
      const isOwner = ['atelier', 'admin_entretiens', 'super_admin'].includes(u.role);
      const okAtelier = isOwner || modules.includes('page_atelier');
      if (allowedRoles.includes(u.role) && u.organization_id && okAtelier) {
        setUser(u);
        setCurrentWorkspace(buildWorkspaceFromUser(u));
      } else {
        localStorage.removeItem('mechanic_token');
        setToken(null);
        setUser(null);
      }
    } catch {
      localStorage.removeItem('mechanic_token');
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [token, buildWorkspaceFromUser]);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password) => {
    try {
      const res = await axios.post(`${API_URL}/api/auth/login`, { email, password });
      const u = res.data.user;
      const allowedRoles = ['mechanic', 'atelier', 'admin_entretiens', 'super_admin'];
      if (!allowedRoles.includes(u.role)) {
        return { success: false, error: 'Ce compte ne peut pas acceder au module Atelier' };
      }
      if (!u.organization_id) {
        return { success: false, error: 'Compte non rattache a un atelier. Contactez votre administrateur.' };
      }
      const modules = u.access_modules || [];
      const isOwner = ['atelier', 'admin_entretiens', 'super_admin'].includes(u.role);
      if (!isOwner && !modules.includes('page_atelier')) {
        return { success: false, error: "Ce compte n'a pas l'acces au module Atelier. Contactez votre administrateur." };
      }
      localStorage.setItem('mechanic_token', res.data.access_token);
      setToken(res.data.access_token);
      setUser(u);
      setCurrentWorkspace(buildWorkspaceFromUser(u));
      return { success: true };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Erreur de connexion' };
    }
  };

  const logout = () => {
    localStorage.removeItem('mechanic_token');
    setToken(null);
    setUser(null);
    setCurrentWorkspace(null);
  };

  return (
    <MechanicAuthContext.Provider value={{
      user, token, loading, login, logout, isAuthenticated: !!user,
      currentWorkspace, hasModule,
    }}>
      {children}
    </MechanicAuthContext.Provider>
  );
};

export const useMechanicAuth = () => {
  const ctx = useContext(MechanicAuthContext);
  if (!ctx) throw new Error('useMechanicAuth must be inside MechanicAuthProvider');
  return ctx;
};

// Onglet Mécanicien de l'admin : mêmes pages que l'ancienne app /atelier, avec la session admin
export const MechanicAuthBridge = ({ user, token, logout, children }) => {
  const value = {
    user,
    token,
    loading: false,
    login: async () => ({ success: false }),
    logout,
    isAuthenticated: !!user,
    currentWorkspace: user?.organization_id ? { id: user.organization_id, name: user.organization_name || 'Mon atelier', is_own: true } : null,
    hasModule: (moduleKey) => {
      if (!user) return false;
      if (['atelier', 'admin_entretiens', 'super_admin', 'admin'].includes(user.role)) return true;
      return (user.access_modules || []).includes(moduleKey);
    },
  };
  return <MechanicAuthContext.Provider value={value}>{children}</MechanicAuthContext.Provider>;
};
